import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { useOfferBuilderContext, WorkingTakerMarket } from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { calculateDiscount } from '@/enterprise/components/OfferBuilder/utils';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { LanguageCode, SUPPORTED_LANGUAGE_CODES } from '@/i18n';
import { useReporting } from '@/reporting';
import useExchangeRates from '@/shared/data/useExchangeRates';
import useSrm from '@/shared/data/useSrm';
import convertCurrency from '@/utils/convertCurrency';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';

const HOW_IT_WORKS_ARTICLE_URLS: Record<LanguageCode, string> = {
  en: 'https://help.c2fo.com/en/articles/13909296-how-it-works',
  de: 'https://help.c2fo.com/de/articles/13909296-so-funktioniert-es',
  es: 'https://help.c2fo.com/es/articles/13909296-como-funciona',
  fr: 'https://help.c2fo.com/fr/articles/13909296-comment-ca-marche',
  it: 'https://help.c2fo.com/it/articles/13909296-come-funziona',
  ja: 'https://help.c2fo.com/ja/articles/13909296-利用方法',
  nl: 'https://help.c2fo.com/nl/articles/13909296-hoe-het-werkt',
  'pt-BR': 'https://help.c2fo.com/pt-BR/articles/13909296-como-funciona',
  ru: 'https://help.c2fo.com/ru/articles/13909296-как-это-работает',
  tr: 'https://help.c2fo.com/tr/articles/13909296-nasil-calisir',
  'zh-CN': 'https://help.c2fo.com/zh-CN/articles/13909296-工作原理',
  'zh-TW': 'https://help.c2fo.com/zh-TW/articles/13909296-工作原理',
};

export interface GetPaidFasterModalProps {
  open: boolean;
  onClose: () => void;
  takerMarkets: TakerMarket[];
  workingTakerMarkets: Record<string, WorkingTakerMarket>;
}

const GetPaidFasterModal = ({ open, onClose, takerMarkets, workingTakerMarkets }: GetPaidFasterModalProps) => {
  const { t, i18n } = useTranslation();
  const languageCode = SUPPORTED_LANGUAGE_CODES.find((code) => code === i18n.language) ?? 'en';
  const howItWorksUrl = HOW_IT_WORKS_ARTICLE_URLS[languageCode];
  const { asCurrency, asNumber, asDateStringText } = useLocaleFormat();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const { trackEnterpriseEvent } = useReporting();
  const srm = useSrm();
  const srmContactHref = srm.calendarLink ?? `mailto:${srm.emailAddress}`;
  const {
    state: { acquisitionOfferProperties },
  } = useOfferBuilderContext();

  useEffectOnce(() => {
    trackEnterpriseEvent('acquisition-offer::get-paid-faster-modal::shown', {
      ...(acquisitionOfferProperties?.acquisitionCorrelationId && {
        acquisitionCorrelationId: acquisitionOfferProperties.acquisitionCorrelationId,
      }),
      ...(acquisitionOfferProperties?.urlReferrer && { urlReferrer: acquisitionOfferProperties.urlReferrer }),
    });
  });

  const stats = useMemo(() => {
    let totalEligibleAmount = 0;
    let totalDiscount = 0;
    let dpeWeightedNumerator = 0;
    let earliestDepositDate: Date | null = null;
    const buyerTotals: Record<string, { name: string; total: number }> = {};

    for (const tm of takerMarkets) {
      const rate = workingTakerMarkets[`${tm.marketUuid}-${tm.takerDivisionUuid}`]?.rate ?? 0;

      const discountInNativeCurrency = calculateDiscount({
        dpeWeightedAvg: tm.eligibleDpeWeightedAvg,
        invoiceAmount: tm.eligibleInvoiceAmount,
        isDiscount: true,
        rate,
      });

      const convertedInvoiceAmount = convertCurrency({
        amount: tm.eligibleInvoiceAmount,
        exchangeRates,
        from: tm.currency,
        to: selectedCurrency,
      });

      const convertedDiscount = convertCurrency({
        amount: discountInNativeCurrency,
        exchangeRates,
        from: tm.currency,
        to: selectedCurrency,
      });

      totalEligibleAmount += convertedInvoiceAmount;
      totalDiscount += convertedDiscount;

      const convertedDpeWeightedSum = convertCurrency({
        amount: tm.eligibleDpeWeightedSum,
        exchangeRates,
        from: tm.currency,
        to: selectedCurrency,
      });
      dpeWeightedNumerator += convertedDpeWeightedSum;

      buyerTotals[tm.makerOrganizationUuid] = {
        name: tm.makerOrganizationName,
        total: (buyerTotals[tm.makerOrganizationUuid]?.total ?? 0) + convertedInvoiceAmount,
      };

      const perMarketDepositDate: Date | null = tm.marketGoodFundsDate ? new Date(tm.marketGoodFundsDate) : null;

      if (perMarketDepositDate) {
        if (!earliestDepositDate || perMarketDepositDate < earliestDepositDate) {
          earliestDepositDate = perMarketDepositDate;
        }
      }
    }

    const estimatedEarlyPayment = totalEligibleAmount - totalDiscount;
    const avgDaysPaidEarly = totalEligibleAmount > 0 ? dpeWeightedNumerator / totalEligibleAmount : 0;

    // Sort buyers by converted eligible amount descending to find the top buyer
    const sortedBuyers = Object.values(buyerTotals).sort((a, b) => b.total - a.total);
    const uniqueBuyerCount = sortedBuyers.length;
    const topBuyerName = sortedBuyers[0]?.name ?? null;

    // primaryBuyerName: used in the title — just the top buyer to keep "Get Paid Faster with X and C2FO." clean
    const primaryBuyerName = topBuyerName;
    // buyerDisplayName: used in the intro — shows "TopBuyer and N more" for multi-buyer offers
    const additionalBuyers = uniqueBuyerCount - 1;
    const buyerDisplayName =
      uniqueBuyerCount <= 1
        ? topBuyerName
        : additionalBuyers === 1
        ? t('getPaidFaster.buyerWithMore', { buyerName: topBuyerName })
        : t('getPaidFaster.buyerWithMorePlural', { buyerName: topBuyerName, count: additionalBuyers });

    return {
      totalEligibleAmount,
      estimatedEarlyPayment,
      avgDaysPaidEarly,
      earliestDepositDate,
      primaryBuyerName,
      buyerDisplayName,
    };
  }, [takerMarkets, workingTakerMarkets, exchangeRates, selectedCurrency, t]);

  const formattedDepositDate = stats.earliestDepositDate ? asDateStringText({ date: stats.earliestDepositDate }) : null;

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalTitleInfo>{t('getPaidFaster.title', { buyerName: stats.primaryBuyerName })}</ModalTitleInfo>
      <ModalContent>
        <div className="flex flex-col gap-4">
          {stats.primaryBuyerName && (
            <div className="rounded-md bg-canvas px-4 py-2 text-center text-sm">
              {t('getPaidFaster.intro', { buyerName: stats.buyerDisplayName })}
            </div>
          )}
          <div className="flex flex-col overflow-hidden rounded-md">
            <div className="grid grid-cols-2 divide-x divide-lightBlue-200 bg-lightBlue-100">
              <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
                <div className="text-xl font-bold">{asCurrency(stats.totalEligibleAmount, selectedCurrency)}</div>
                <div className="text-sm font-semibold text-text-secondary">{t('getPaidFaster.totalInvoiceAmount')}</div>
              </div>
              <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
                <div className="text-xl font-bold">
                  {asNumber({ value: handleNaN(stats.avgDaysPaidEarly), decimals: 0 })}
                </div>
                <div className="text-sm font-semibold text-text-secondary">{t('getPaidFaster.avgDaysPaidEarly')}</div>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center bg-blue-500 px-4 py-3">
              <div className="text-xl font-bold text-white">
                {asCurrency(stats.estimatedEarlyPayment, selectedCurrency)}
              </div>
              <div className="text-center text-sm font-semibold text-white">
                {t('getPaidFaster.estimatedEarlyPayment')}
              </div>
            </div>
          </div>
          {formattedDepositDate && (
            <p className="text-sm">
              {t('getPaidFaster.body', {
                daysPaidEarly: Math.round(stats.avgDaysPaidEarly),
                depositDate: formattedDepositDate,
              })}
            </p>
          )}
          <div className="flex flex-col items-center justify-center rounded-md bg-canvas p-4 text-center">
            <p className="mb-1 text-sm font-semibold">{t('getPaidFaster.notReady')}</p>
            <p className="text-sm">
              <a href={srmContactHref} target="_blank" rel="noreferrer" className="font-semibold text-blue-500">
                {t('getPaidFaster.contactSrm')}
              </a>{' '}
              {t('getPaidFaster.toFindOut')}
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              {t('getPaidFaster.wantToKnow')}{' '}
              <a href={howItWorksUrl} target="_blank" rel="noreferrer" className="font-semibold text-blue-500">
                {t('getPaidFaster.seeHow')}
              </a>
            </p>
          </div>
          <p className="text-sm text-text-secondary">{t('getPaidFaster.clickingClose')}</p>
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} variant="secondary">
          {t('getPaidFaster.close')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default GetPaidFasterModal;
